import { useCallback, useEffect, useState } from "react";
import { listEvaluatorVersions } from "../api";
import type { Evaluator } from "../types";

interface UseEvaluatorVersionsResult {
  /** Every stored version, newest first, including soft-deleted ones. */
  versions: Evaluator[];
  isLoading: boolean;
  error: string | null;
  reload: () => void;
}

/**
 * Load the full version chain for one evaluator group.
 *
 * `GET /api/v1/evaluators?group_id=…` has always returned this; nothing called it. The
 * fetch is keyed on the group and re-runs after a save so the panel shows the version
 * that was just created.
 */
export function useEvaluatorVersions(
  groupId: string | undefined,
  /**
   * Change to force a refetch. The detail pane passes the active version's id: a save
   * inserts a new row under the same group_id, so a changed id means a changed chain.
   */
  refreshToken: string | number = 0
): UseEvaluatorVersionsResult {
  const [versions, setVersions] = useState<Evaluator[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [manualToken, setManualToken] = useState(0);

  const reload = useCallback(() => setManualToken((n) => n + 1), []);

  useEffect(() => {
    if (!groupId) {
      setVersions([]);
      setError(null);
      setIsLoading(false);
      return;
    }

    // Guards against a slow response for a previously selected evaluator landing
    // after the user has already clicked onto a different one.
    let cancelled = false;
    setIsLoading(true);
    setError(null);

    listEvaluatorVersions(groupId)
      .then((data) => {
        if (cancelled) return;
        setVersions(data);
      })
      .catch((err: Error) => {
        if (cancelled) return;
        setVersions([]);
        setError(err.message || "Failed to load version history");
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [groupId, refreshToken, manualToken]);

  return { versions, isLoading, error, reload };
}
